import Phaser from 'phaser';
import { HEROES, HeroDef } from '@/data/heroes';
import { GameState, type Difficulty, type GameMode } from '@/state/GameState';
import { CharSelectState } from '@/systems/CharSelectState';
import {
  getSaveManager,
  ACTIVE_SLOT,
  DEFAULT_UNLOCKED_HEROES,
  DEFAULT_UNLOCKED_DIFFICULTIES,
} from '@/systems/SaveManager';
import { hasCutscene } from '@/data/cutscene';
import { nextStageRoute, modeStartRoute } from '@/systems/stageFlow';
import { BOSS_RUSH_LIVES } from '@/systems/BossRushSequence';
import { MusicDirectorAdapter } from '@/systems/MusicDirectorAdapter';
import { createPhaserMusicFactory } from '@/systems/createPhaserMusicFactory';

// ─── Layout constants ─────────────────────────────────────────────────────────

const CARD_W = 162;
const CARD_H = 330;
const CARD_SPACING = 185;
const PORTRAIT_H = 108;
const BAR_MAX_W = 110;
const BAR_H = 8;
const FONT_MONO = '"Courier New", monospace';

interface PortraitCrop {
  x: number;
  y: number;
  w: number;
  h: number;
}

const HEADSHOT_CROPS: Readonly<Record<string, PortraitCrop>> = {
  ray: { x: 262, y: 44, w: 392, h: 270 },
  errol: { x: 242, y: 70, w: 450, h: 310 },
  kris: { x: 268, y: 54, w: 380, h: 262 },
  priya: { x: 260, y: 174, w: 405, h: 279 },
  ghost: { x: 326, y: 44, w: 330, h: 227 },
  'pm-halloway': { x: 62, y: 25, w: 132, h: 91 },
};

/** Scale of a card at visual distance d from the selected card (0 = selected). */
function cardScale(d: number): number {
  return Math.max(0.62, 1 - 0.13 * d);
}

/** Alpha of a card at visual distance d. */
function cardAlpha(d: number): number {
  return Math.max(0.35, 1 - 0.22 * d);
}

function clampCrop(srcW: number, srcH: number, crop: PortraitCrop): PortraitCrop {
  const x = Math.max(0, Math.min(srcW - 1, crop.x));
  const y = Math.max(0, Math.min(srcH - 1, crop.y));
  return {
    x,
    y,
    w: Math.max(1, Math.min(srcW - x, crop.w)),
    h: Math.max(1, Math.min(srcH - y, crop.h)),
  };
}

// ─── Scene ───────────────────────────────────────────────────────────────────

/**
 * CharSelectScene — MY-1453
 *
 * Roundabout carousel: all hero cards always visible. Selected card is centred
 * and enlarged (scale 1.0); others shrink + dim by distance. Smooth tweens on
 * cursor movement. P1 + P2 cursors for co-op. Each card shows:
 *   - Portrait placeholder with idle-bob tween
 *   - Name + epithet
 *   - 2-line bio (heritage + style)
 *   - 4 stat bars (SPD / PWR / RNG / TEC, 0–10 scale)
 */
const DIFFICULTIES: readonly Difficulty[] = ['pint', 'pub', 'brawl', 'manor'];
const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  pint: 'PINT',
  pub: 'PUB',
  brawl: 'BRAWL',
  manor: 'MANOR',
};

export class CharSelectScene extends Phaser.Scene {
  private selectState!: CharSelectState;
  private cards: Phaser.GameObjects.Container[] = [];
  private unlockedHeroIds: readonly string[] = DEFAULT_UNLOCKED_HEROES;
  private unlockedDifficulties: readonly Difficulty[] = DEFAULT_UNLOCKED_DIFFICULTIES;
  private selectedDifficulty: Difficulty = 'pub';
  private diffText!: Phaser.GameObjects.Text;
  private p1CursorText!: Phaser.GameObjects.Text;
  private p2CursorText!: Phaser.GameObjects.Text;
  private music!: MusicDirectorAdapter;

  constructor() {
    super({ key: 'CharSelectScene' });
  }

  create(data?: { gameMode?: GameMode; coop?: boolean }): void {
    const { width, height } = this.scale;
    GameState.newRun();
    // Re-apply a non-campaign mode (e.g. survival) chosen at the title screen,
    // since newRun() above resets gameMode to 'campaign'.
    if (data?.gameMode) GameState.setGameMode(data.gameMode);
    // Re-apply co-op (Title CO-OP entry) — newRun() above cleared coopMode.
    if (data?.coop) GameState.enableCoop();
    if (data?.gameMode === 'boss-rush') GameState.setLives(BOSS_RUSH_LIVES);

    const saveData = getSaveManager().load(ACTIVE_SLOT);
    this.unlockedHeroIds = saveData?.unlockedHeroIds ?? [...DEFAULT_UNLOCKED_HEROES];
    this.unlockedDifficulties = saveData?.unlockedDifficulties ?? [
      ...DEFAULT_UNLOCKED_DIFFICULTIES,
    ];
    const saved = saveData?.lastDifficulty;
    this.selectedDifficulty = saved && this.unlockedDifficulties.includes(saved) ? saved : 'pub';
    GameState.setDifficulty(this.selectedDifficulty);

    const lockedIndices = HEROES.map((h, i) => (this.isHeroLocked(h) ? i : -1)).filter(
      (i) => i >= 0,
    );

    this.selectState = new CharSelectState({
      heroCount: HEROES.length,
      lockedIndices,
      coopMode: GameState.current.coopMode,
    });

    this.buildBackground(width, height);
    this.buildTitle(width);
    this.buildCards(width, height);
    this.buildCursorLabels(width, height);
    this.buildHint(width, height);
    this.buildDifficulty(width, height);
    this.bindKeys();
    this.refreshLayout(false);

    this.music = new MusicDirectorAdapter(undefined, createPhaserMusicFactory(this));
    this.music.onSceneChange('charSelect');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.music.stopAll());
  }

  update(_time: number, delta: number): void {
    this.music.update(delta);
  }

  shutdown(): void {
    this.music.stopAll();
  }

  // ─── Background ────────────────────────────────────────────────────────────

  private buildBackground(width: number, height: number): void {
    this.add.rectangle(width / 2, height / 2, width, height, 0x0b0e14);
    // Subtle amber glow at the floor
    this.add.rectangle(width / 2, height - 18, width, 36, 0xff6b00).setAlpha(0.07);
  }

  // ─── Title ─────────────────────────────────────────────────────────────────

  private buildTitle(width: number): void {
    this.add
      .text(width / 2, 42, 'SELECT YOUR FIGHTER', {
        fontFamily: FONT_MONO,
        fontSize: '28px',
        color: '#ffba08',
        letterSpacing: 4,
      })
      .setOrigin(0.5);
  }

  // ─── Difficulty selector ────────────────────────────────────────────────

  private buildDifficulty(width: number, height: number): void {
    this.diffText = this.add
      .text(width / 2, height - 52, '', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: '#06d6a0',
      })
      .setOrigin(0.5);
    this.refreshDifficulty();
    this.input.keyboard?.on('keydown-T', () => this.cycleDifficulty());
  }

  private refreshDifficulty(): void {
    this.diffText.setText(`DIFFICULTY: ${DIFFICULTY_LABEL[this.selectedDifficulty]}   [T] change`);
  }

  /** Cycle to the next unlocked difficulty (DIFFICULTIES order), apply to GameState. */
  private cycleDifficulty(): void {
    const unlocked = DIFFICULTIES.filter((d) => this.unlockedDifficulties.includes(d));
    if (unlocked.length <= 1) return;
    const i = unlocked.indexOf(this.selectedDifficulty);
    this.selectedDifficulty = unlocked[(i + 1) % unlocked.length] ?? this.selectedDifficulty;
    GameState.setDifficulty(this.selectedDifficulty);
    this.refreshDifficulty();
  }

  // ─── Cards ─────────────────────────────────────────────────────────────────

  private buildCards(width: number, height: number): void {
    this.cards = [];
    const centerX = width / 2;
    const centerY = height / 2 + 24;

    HEROES.forEach((hero, i) => {
      const card = this.buildCard(hero);
      // Initial position snapped: will be adjusted in refreshLayout
      card.setPosition(centerX + (i - this.selectState.p1Index) * CARD_SPACING, centerY);
      this.cards.push(card);
    });
  }

  private isHeroLocked(h: HeroDef): boolean {
    return h.lockedByDefault && !this.unlockedHeroIds.includes(h.id);
  }

  private buildCard(hero: HeroDef): Phaser.GameObjects.Container {
    const locked = this.isHeroLocked(hero);
    const accentAlpha = locked ? 0.1 : 0.85;

    // Background panel
    const bg = this.add
      .rectangle(0, 0, CARD_W, CARD_H, hero.color, accentAlpha)
      .setStrokeStyle(2, locked ? 0x444444 : 0xffffff, locked ? 0.12 : 0.85);

    // Portrait area
    const portraitY = -CARD_H / 2 + PORTRAIT_H / 2 + 4;
    const portrait = this.add
      .rectangle(0, portraitY, CARD_W - 14, PORTRAIT_H, hero.color, locked ? 0.06 : 0.45)
      .setStrokeStyle(1, 0xffffff, locked ? 0.04 : 0.3);

    // Hero art portrait when the texture is loaded; falls back to the ☰ silhouette.
    const hasArt = !locked && hero.assetId != null && this.textures.exists(hero.assetId);
    const silhouette = this.add
      .text(0, portraitY, locked ? '?' : '☰', {
        fontFamily: FONT_MONO,
        fontSize: locked ? '30px' : '38px',
        color: locked ? '#333333' : '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(locked ? 0.25 : 0.5)
      .setVisible(!hasArt);

    let portraitImg: Phaser.GameObjects.Image | null = null;
    if (hasArt) {
      const src = this.textures
        .get(hero.assetId as string)
        .getSourceImage() as CanvasImageSource & {
        width: number;
        height: number;
      };
      const maxW = CARD_W - 18;
      const maxH = PORTRAIT_H - 6;
      const crop = clampCrop(
        src.width,
        src.height,
        HEADSHOT_CROPS[hero.assetId as string] ?? {
          x: Math.round(src.width * 0.18),
          y: Math.round(src.height * 0.04),
          w: Math.round(src.width * 0.64),
          h: Math.round(src.width * 0.64 * (maxH / maxW)),
        },
      );
      const headshotKey = `${hero.assetId}-headshot`;
      if (!this.textures.exists(headshotKey)) {
        const headshot = this.textures.createCanvas(headshotKey, maxW, maxH);
        if (headshot) {
          const ctx = headshot.getContext();
          ctx.imageSmoothingEnabled = false;
          ctx.clearRect(0, 0, maxW, maxH);
          ctx.drawImage(src, crop.x, crop.y, crop.w, crop.h, 0, 0, maxW, maxH);
          headshot.refresh();
        }
      }
      portraitImg = this.add
        .image(
          0,
          portraitY,
          this.textures.exists(headshotKey) ? headshotKey : (hero.assetId as string),
        )
        .setOrigin(0.5);
    }

    // Idle-bob tween on portrait (placeholder animation)
    if (!locked) {
      this.tweens.add({
        targets: portraitImg ?? portrait,
        y: { from: portraitY - 3, to: portraitY + 3 },
        duration: 850,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Name
    const nameY = -CARD_H / 2 + PORTRAIT_H + 16;
    const nameText = this.add
      .text(0, nameY, locked ? '??? LOCKED' : hero.name, {
        fontFamily: FONT_MONO,
        fontSize: '11px',
        color: locked ? '#444444' : '#ffffff',
        align: 'center',
        wordWrap: { width: CARD_W - 10 },
      })
      .setOrigin(0.5);

    // Epithet
    const epithetText = this.add
      .text(0, nameY + 20, locked ? 'Clear Stage 1' : hero.epithet, {
        fontFamily: FONT_MONO,
        fontSize: '10px',
        color: locked ? '#333333' : '#ffba08',
        align: 'center',
      })
      .setOrigin(0.5);

    // Bio line 1: heritage
    const bio1 = this.add
      .text(0, nameY + 38, locked ? 'to unlock' : hero.heritage, {
        fontFamily: FONT_MONO,
        fontSize: '9px',
        color: locked ? '#333333' : '#8a90a0',
        align: 'center',
        wordWrap: { width: CARD_W - 10 },
      })
      .setOrigin(0.5);

    // Bio line 2: style
    const bio2 = this.add
      .text(0, nameY + 52, locked ? '' : hero.style, {
        fontFamily: FONT_MONO,
        fontSize: '9px',
        color: locked ? '#333333' : '#6a707e',
        align: 'center',
        wordWrap: { width: CARD_W - 10 },
      })
      .setOrigin(0.5);

    // Stat bars
    const barsContainer = this.buildStatBars(hero, locked, nameY + 74);

    return this.add.container(0, 0, [
      bg,
      portrait,
      silhouette,
      ...(portraitImg ? [portraitImg] : []),
      nameText,
      epithetText,
      bio1,
      bio2,
      barsContainer,
    ]);
  }

  /** Draws 4 labelled stat bars into a sub-container, returned for embedding. */
  private buildStatBars(
    hero: HeroDef,
    locked: boolean,
    topY: number,
  ): Phaser.GameObjects.Container {
    const labels = ['SPD', 'PWR', 'RNG', 'TEC'] as const;
    const values = [hero.stats.speed, hero.stats.power, hero.stats.range, hero.stats.technique];
    const rowH = 18;
    const barX = -BAR_MAX_W / 2;
    const gfx = this.add.graphics();
    const children: Phaser.GameObjects.GameObject[] = [gfx];

    if (!locked) {
      labels.forEach((label, i) => {
        const y = topY + i * rowH;
        const val = Math.min(10, Math.max(0, values[i] ?? 0));
        const fillW = Math.round((val / 10) * BAR_MAX_W);

        // Track
        gfx.fillStyle(0x1a1f28, 1);
        gfx.fillRect(barX, y + 4, BAR_MAX_W, BAR_H);
        // Fill
        gfx.fillStyle(0xffba08, 0.88);
        gfx.fillRect(barX, y + 4, fillW, BAR_H);

        children.push(
          this.add
            .text(barX - 4, y + 4, label, {
              fontFamily: FONT_MONO,
              fontSize: '8px',
              color: '#9aa0b0',
            })
            .setOrigin(1, 0),
        );
      });
    }

    return this.add.container(0, 0, children);
  }

  // ─── Cursor labels ─────────────────────────────────────────────────────────

  private buildCursorLabels(width: number, height: number): void {
    const centerY = height / 2 + 24;
    const yAbove = centerY - CARD_H / 2 - 26;

    this.p1CursorText = this.add
      .text(width / 2, yAbove, 'P1 ▼', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: '#ef233c',
      })
      .setOrigin(0.5);

    this.p2CursorText = this.add
      .text(width / 2, yAbove - 20, 'P2 ▼', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: '#00d4ff',
      })
      .setOrigin(0.5)
      .setVisible(GameState.current.coopMode);
  }

  // ─── Hint bar ──────────────────────────────────────────────────────────────

  private buildHint(width: number, height: number): void {
    const coop = GameState.current.coopMode;
    const hint = coop
      ? '← → P1   A/D P2   [SPACE] P1 Confirm   [K] P2 Confirm'
      : '← →  Move      [SPACE / ENTER]  Confirm';

    this.add
      .text(width / 2, height - 30, hint, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: '#555e6e',
      })
      .setOrigin(0.5);

    this.add
      .text(width - 10, height - 10, 'MY-1453', {
        fontFamily: FONT_MONO,
        fontSize: '10px',
        color: '#252a33',
      })
      .setOrigin(1, 1);
  }

  // ─── Key bindings ──────────────────────────────────────────────────────────

  private bindKeys(): void {
    const kb = this.input.keyboard!;

    // P1
    kb.on('keydown-LEFT', () => this.onMove(1, -1));
    kb.on('keydown-RIGHT', () => this.onMove(1, 1));
    kb.on('keydown-SPACE', () => this.onConfirm(1));
    kb.on('keydown-ENTER', () => this.onConfirm(1));
    kb.on('keydown-ESC', () => this.onCancel(1));
    kb.on('keydown-BACKSPACE', () => this.onCancel(1));

    // P2 (active only in co-op; keys safe to add unconditionally, state guards)
    if (GameState.current.coopMode) {
      kb.on('keydown-A', () => this.onMove(2, -1));
      kb.on('keydown-D', () => this.onMove(2, 1));
      kb.on('keydown-K', () => this.onConfirm(2));
      kb.on('keydown-L', () => this.onCancel(2));
    }
  }

  // ─── Input handlers ────────────────────────────────────────────────────────

  private onMove(player: 1 | 2, dir: number): void {
    if (this.selectState.move(player, dir)) this.refreshLayout(true);
  }

  private onConfirm(player: 1 | 2): void {
    this.selectState.confirm(player);
    this.refreshLayout(true);
    if (this.selectState.readyToStart) {
      // Brief flash before transition
      this.time.delayedCall(200, () => this.startGame());
    }
  }

  private onCancel(player: 1 | 2): void {
    this.selectState.cancel(player);
    this.refreshLayout(true);
  }

  private startGame(): void {
    const hero = HEROES[this.selectState.p1Index];
    if (!hero || this.isHeroLocked(hero)) return;
    GameState.selectHero(hero.id);
    if (GameState.current.coopMode) {
      const hero2 = HEROES[this.selectState.p2Index];
      if (hero2 && !this.isHeroLocked(hero2)) {
        GameState.selectP2Hero(hero2.id);
      }
    }
    const stage = GameState.current.stage;
    const mode = GameState.current.gameMode;
    const route =
      mode === 'campaign'
        ? nextStageRoute(stage, hasCutscene(`stage${stage}-intro`))
        : modeStartRoute(mode, hasCutscene(`stage${stage}-intro`));
    this.scene.start(route.sceneKey, route.data);
  }

  // ─── Layout refresh ────────────────────────────────────────────────────────

  /**
   * Reposition + re-scale all cards around the P1 cursor index.
   * animate=true uses tweens; animate=false snaps (used on first create).
   */
  private refreshLayout(animate: boolean): void {
    const { width, height } = this.scale;
    const centerX = width / 2;
    const centerY = height / 2 + 24;
    const p1i = this.selectState.p1Index;
    const p2i = this.selectState.p2Index;

    this.cards.forEach((card, i) => {
      const dist = Math.abs(i - p1i);
      const targetX = centerX + (i - p1i) * CARD_SPACING;
      const targetScale = cardScale(dist);
      const targetAlpha = cardAlpha(dist);

      if (animate) {
        this.tweens.add({
          targets: card,
          x: targetX,
          scaleX: targetScale,
          scaleY: targetScale,
          alpha: targetAlpha,
          duration: 160,
          ease: 'Quad.easeOut',
        });
      } else {
        card.setPosition(targetX, centerY);
        card.setScale(targetScale);
        card.setAlpha(targetAlpha);
      }
    });

    // P1 cursor always at screen centre (tracks with the carousel)
    this.p1CursorText.setX(centerX);
    this.p1CursorText.setText(this.selectState.p1Confirmed ? 'P1 ✓' : 'P1 ▼');

    // P2 cursor offset from centre by P2's distance from P1
    if (GameState.current.coopMode) {
      const p2OffsetX = (p2i - p1i) * CARD_SPACING;
      this.p2CursorText.setX(centerX + p2OffsetX).setVisible(true);
      this.p2CursorText.setText(this.selectState.p2Confirmed ? 'P2 ✓' : 'P2 ▼');
    }
  }
}
